import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'

/**
 * Canonical tone → Badge variant map.
 * Import this map from other modules instead of defining local *_VARIANTS / *_CONFIG records.
 *
 * StatusBadge supersedes:
 *   - STATUS_CONFIG in dashboard/engine-status-card.tsx (keep existing until callsites migrate)
 */
export const TONE_VARIANT = {
  success: 'emerald',
  danger: 'rose',
  warning: 'amber',
  neutral: 'outline',
  info: 'indigo',
} as const satisfies Record<StatusBadgeTone, string>

export type StatusBadgeTone = 'success' | 'danger' | 'warning' | 'neutral' | 'info'

interface StatusBadgeProps {
  tone: StatusBadgeTone
  /** Render a colored dot before the label. */
  dot?: boolean
  /**
   * `pill` (default) = full-radius shell, the long-standing app-wide default.
   * `rect` = Figma's "Badge color" 6-8px-radius shell — use only where a
   * specific instance (e.g. NextActionsCard's priority/effort chips) is
   * verified against that master type, not as a general replacement.
   */
  shape?: 'pill' | 'rect'
  className?: string
  children: React.ReactNode
}

const DOT_CLASS: Record<StatusBadgeTone, string> = {
  success: 'bg-emerald',
  danger: 'bg-rose',
  warning: 'bg-amber',
  neutral: 'bg-muted-foreground',
  info: 'bg-info',
}

/**
 * StatusBadge — canonical state badge atom.
 *
 * Use for STATUS (on/off/pending/error/leading/trailing).
 * Do NOT use for category labels — use <Tag> for those (no dot, soft fill, no state).
 *
 * Supersedes STATUS_CONFIG in engine-status-card.tsx (do not delete it yet — callsites migrate on page rollout).
 */
export function StatusBadge({
  tone,
  dot = false,
  shape = 'pill',
  className,
  children,
}: StatusBadgeProps): React.ReactElement {
  const variant = TONE_VARIANT[tone] as 'emerald' | 'rose' | 'amber' | 'outline' | 'indigo'

  return (
    <Badge
      variant={variant}
      shape={shape}
      className={cn('inline-flex items-center gap-1.5', className)}
    >
      {dot && (
        <span
          className={cn('size-1.5 shrink-0 rounded-full', DOT_CLASS[tone])}
          aria-hidden="true"
        />
      )}
      {children}
    </Badge>
  )
}
